import { useMemo } from "react";
import { useIntl } from "react-intl";
import { FieldHelperProps, useFormikContext } from "formik";
import { Checkbox, FormControlLabel, Typography } from "@mui/material";
import { GridRowModel } from "@mui/x-data-grid-pro";

import { EditFormDetails } from "../interfaces";
import { TooltipLabel } from "../../components/Tooltips";

const CheckBoxInput = ({
  filter,
  field,
  meta,
  classes,
  helpers,
  row,
}: {
  filter: EditFormDetails;
  field: any;
  helpers: FieldHelperProps<any>;
  meta: any;
  classes: any;
  row?: GridRowModel;
}) => {
  const intl = useIntl();
  const { values, setFieldValue } = useFormikContext<any>();
  const isChecked = meta.value ? meta.value : false;

  // Disabled input if input on which it depends is empty
  const isDisabled: boolean = useMemo(() => {
    let disabled = false;

    if (filter.readonly) {
      disabled = true;
    } else if (filter.enableField && values) {
      if (!values[filter.enableField as string]) {
        disabled = true;
      }
      // New way to check if should be disable, should replace all other ways
    } else if (filter.relatedInputAction && values) {
      if (
        filter.relatedInputAction.action === "enable" &&
        typeof filter.relatedInputAction.relatedInput === "string" &&
        values[filter.relatedInputAction.relatedInput.split(".").join("__")]
      ) {
        disabled = true;
      } else {
        disabled = false;
      }
    }

    return disabled;
  }, [filter.enableField, filter.relatedInputAction, filter.readonly, values]);

  // A box disabled by the form state shows unticked and submits false (see
  // getSubmitValue), but the form value is left alone: should the state go
  // back to normal, the user's own choice comes back with it.
  const isDisabledByState = !!filter.disabledIf?.(row, values);

  const handleChange = (newValue: boolean) => {
    // Clean seletor (in case of dynamic values) if selector on which it depends change
    if (filter.watchedBy && Array.isArray(filter.watchedBy)) {
      filter.watchedBy.forEach((input) => {
        setFieldValue(input.split(".").join("__"), null);
      });
    } else if (filter.watchedBy) {
      // Get path of watchedBy input (take into account nested field)
      const fieldPathArray = field.name.split(".");
      const parentPath = fieldPathArray.slice(0, -1);
      const transformedWatchKey = filter.watchedBy.replace(/\./g, "__");
      const watchedFieldPath = [...parentPath, transformedWatchKey];

      setFieldValue(watchedFieldPath.join("."), null);
    }

    return helpers.setValue(newValue);
  };

  return (
    <>
      <FormControlLabel
        className={`${classes.checkbox} ${
          meta.touched && !!meta.error ? "error" : ""
        }`}
        control={
          <Checkbox
            {...field}
            checked={isDisabledByState ? false : isChecked}
            onChange={(_, newValue) => handleChange(newValue)}
            disabled={isDisabled || isDisabledByState}
          />
        }
        disabled={filter.readonly || isDisabledByState}
        label={
          filter.tooltip ? (
            <TooltipLabel
              label={intl.formatMessage({ id: filter.label })}
              tooltipLabel={intl.formatMessage({ id: filter.tooltip })}
              translateTooltip={false}
            />
          ) : (
            intl.formatMessage({ id: filter.label })
          )
        }
      />
      {isDisabledByState && filter.disabledHint && (
        <Typography
          component="p"
          variant="caption"
          sx={{ color: "text.secondary", margin: 0 }}
        >
          {intl.formatMessage({ id: filter.disabledHint })}
        </Typography>
      )}
      {meta.touched && !!meta.error && (
        <Typography component={"p"} sx={{ color: "error.main", margin: 0 }}>
          {meta.error}
        </Typography>
      )}
    </>
  );
};

export default CheckBoxInput;
